import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import {
    Subsets,
    subsetSetter,
    subsetGetter,
    subsetUpdateSetter,
    SubsetGetterProps,
    subsetTotalGetter,
    SubsetTotalGetterProps,
} from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import { getConfig, klinikenApi } from "@/api";
import cloneDeep from "lodash/cloneDeep";

export type Underlag = {
    id: string;
    namn: string;
};

type State = BaseState & {
    underlag: Map<Underlag["id"], Underlag>;
};

const getDefaultState: () => State = () => ({
    underlag: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "underlag";
const baseUrl = "skreg/underlag/";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        underlag: (state: State) => [...state.underlag.values()],
        underlagById:
            (state: State) =>
            ({ id }: { id?: string }) => {
                if (!id) return null;
                return state.underlag.get(id);
            },
        underlagSubset: (state: State) => (props: SubsetGetterProps<State>) =>
            subsetGetter<State>({ ...props, state, collectionName: "underlag" }),
        // Antalet rader som finns för subsetet, det värde paginatorn räknar på
        underlagSubsetTotal: (state: State) => (props: SubsetTotalGetterProps<State>) =>
            subsetTotalGetter<State>({ ...props, state }),
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addUnderlag: (
            state: State,
            {
                offset,
                subsetName = "default",
                underlag = [],
            }: {
                offset: number;
                subsetName: keyof Subsets;
                underlag: Underlag[];
            }
        ) =>
            subsetSetter<State, Underlag>({
                offset,
                subsetName,
                state,
                collectionName: "underlag",
                items: underlag,
                includeDefaultSubset: true,
            }),
        updateUnderlag: (
            state: State,
            {
                underlag,
                offset,
            }: {
                underlag: Underlag[];
                offset: number;
            }
        ) =>
            subsetUpdateSetter<State, Underlag>({
                offset,
                state,
                collectionName: "underlag",
                items: underlag,
                includeDefaultSubset: true,
            }),
    },
    actions: {
        ...baseActions({ moduleName }),
        fetchUnderlag: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 20,
                page = 1,
                filterParams = {},
                forceRefresh = false,
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                forceRefresh?: boolean;
            } = {}
        ) => {
            try {
                const { subsetName, results, total, nextPage, offset } = await fetchItems<State, Underlag>({
                    filterParams,
                    state,
                    page,
                    limit,
                    forceRefresh,
                    collectionName: "underlag",
                    apiUrl: baseUrl,
                });

                if (results) {
                    commit("addUnderlag", { subsetName, offset, underlag: results });
                }
                if (typeof total === "number") {
                    commit("setTotal", { total, subsetName });
                }

                return { subsetName, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        createUnderlag: async (
            { commit, state }: BaseActionsProps<State>,
            { underlag }: { underlag: Partial<Underlag> }
        ) => {
            try {
                const response = await klinikenApi.post(baseUrl, underlag, getConfig({}));

                commit("addUnderlag", {
                    offset: Object.keys(state.subsets.newlyCreated.ids).length,
                    subsetName: "newlyCreated",
                    underlag: [response.data],
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        updateUnderlag: async ({ commit, state }: BaseActionsProps<State>, { underlag }: { underlag: Underlag }) => {
            try {
                const response = await klinikenApi.put(`${baseUrl}${underlag.id}/`, underlag, getConfig({}));

                commit("updateUnderlag", {
                    underlag: [response.data],
                    offset: Object.keys(state.subsets.newlyUpdated.ids).length,
                });
                return response.data;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
    },
};
